import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchContactTimeline, type TimelineEvent } from '../lib/identified-visitors-api';
import { formatDate, formatTime } from '../lib/datetime';
import styles from './ContactTimeline.module.css';

// One contact's activity across every channel, newest first, grouped by day:
// page views, chats, tickets and identity events. Older pages load on demand.

const CHANNEL: Record<string, string> = { web: 'website', whatsapp: 'WhatsApp', api: 'API', instagram: 'Instagram', messenger: 'Messenger' };

function channelLabel(channel?: string): string {
  return channel ? CHANNEL[channel] ?? channel : 'website';
}

/** Path + query of a visited URL (the host is always the site's own). */
function pagePath(url?: string): string {
  if (!url) return '';
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}` || '/';
  } catch {
    return url;
  }
}

function describe(e: TimelineEvent): { icon: string; text: string } {
  switch (e.kind) {
    case 'page_view':
      return { icon: '↳', text: `Viewed ${pagePath(e.url)}` };
    case 'chat_started':
      return {
        icon: '●',
        text: `${e.medium === 'video' ? 'Video call' : 'Chat'} started on ${channelLabel(e.channel)}${e.agentName ? ` · ${e.agentName}` : ''}`,
      };
    case 'chat_ended':
      return { icon: '○', text: `${e.medium === 'video' ? 'Video call' : 'Chat'} ended${e.endReason ? ` (${e.endReason.replace(/-/g, ' ')})` : ''}` };
    case 'ticket_opened':
      return { icon: '◆', text: `Ticket #${e.ticketNumber} opened: ${e.subject ?? ''}` };
    case 'ticket_resolved':
      return { icon: '✓', text: `Ticket #${e.ticketNumber} resolved` };
    case 'ticket_closed':
      return { icon: '✕', text: `Ticket #${e.ticketNumber} closed` };
    case 'identified':
      return { icon: '★', text: 'Shared their contact details' };
    case 'channel_linked':
      return { icon: '⇄', text: `Reached us on ${channelLabel(e.channel)} — linked to this contact` };
  }
}

export function ContactTimeline({ visitorId }: { visitorId: string }) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The contact whose timeline is on screen, so an older page that lands
  // after the agent switched contacts is dropped.
  const current = useRef(visitorId);

  // Newest page. A response for a superseded request (contact switched, or
  // StrictMode's second effect run) is ignored rather than raced.
  useEffect(() => {
    current.current = visitorId;
    let cancelled = false;
    setEvents([]);
    setNextBefore(null);
    setError(null);
    setLoading(true);
    fetchContactTimeline(visitorId)
      .then((page) => {
        if (cancelled) return;
        setEvents(page.events);
        setNextBefore(page.nextBefore);
      })
      .catch(() => {
        if (!cancelled) setError('Couldn’t load this contact’s activity.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visitorId]);

  async function loadOlder() {
    if (nextBefore === null || loading) return;
    const forVisitor = visitorId;
    setLoading(true);
    try {
      const page = await fetchContactTimeline(forVisitor, nextBefore);
      if (current.current !== forVisitor) return;
      // Pages overlap by the events sharing a boundary millisecond; keep each once.
      setEvents((prev) => {
        const have = new Set(prev.map((e) => e.id));
        return [...prev, ...page.events.filter((e) => !have.has(e.id))];
      });
      setNextBefore(page.nextBefore);
    } catch {
      if (current.current === forVisitor) setError('Couldn’t load older activity.');
    } finally {
      if (current.current === forVisitor) setLoading(false);
    }
  }

  if (error) return <p className={styles.error}>{error}</p>;
  if (loading && events.length === 0) return <p className={styles.muted}>Loading activity…</p>;
  if (events.length === 0) return <p className={styles.muted}>No activity recorded for this contact yet.</p>;

  // Group consecutive events by calendar day (events are newest first).
  const days: Array<{ day: string; items: TimelineEvent[] }> = [];
  for (const e of events) {
    const day = formatDate(e.at);
    if (days[days.length - 1]?.day !== day) days.push({ day, items: [] });
    days[days.length - 1]!.items.push(e);
  }

  return (
    <div className={styles.root} aria-label="Contact activity">
      {days.map(({ day, items }) => (
        <section key={day} className={styles.day}>
          <h4 className={styles.dayLabel}>{day}</h4>
          <ol className={styles.list}>
            {items.map((e) => {
              const { icon, text } = describe(e);
              return (
                <li key={e.id} className={styles.item} data-kind={e.kind}>
                  <time className={styles.time} dateTime={new Date(e.at).toISOString()}>
                    {formatTime(e.at)}
                  </time>
                  <span className={styles.icon} aria-hidden>{icon}</span>
                  <span className={styles.text} title={e.kind === 'page_view' ? e.url : undefined}>{text}</span>
                  {e.kind === 'chat_started' && e.sessionId ? (
                    <Link className={styles.link} to={`/conversation/${e.sessionId}`}>Open →</Link>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
      {nextBefore !== null ? (
        <button type="button" className={styles.more} disabled={loading} onClick={() => void loadOlder()}>
          {loading ? 'Loading…' : 'Load older activity'}
        </button>
      ) : (
        <p className={styles.muted}>Start of this contact’s history.</p>
      )}
    </div>
  );
}
